// P2b tanda 5 → 5b — editor de la tabla de medidas como HOJA DE CÁLCULO: la
// tabla editable ES la vista de la tienda (sin vista previa aparte). Celdas
// para encabezados, tallas y VALORES; Tab/Enter navegan como en una hoja;
// pegar desde Excel (tabulador + salto de línea) rellena (utils/tablaMedidas.
// aplicarPegado). Celda vacía se guarda vacía y la tienda la muestra "—" (acá
// el placeholder "—" lo dice). La normalización (reglas del theme) corre al
// GUARDAR, no al tipear — una columna a medio nombrar no borra sus valores.
// La nota de unidad (props nota/onNotaChange) solo la usa el editor de guías:
// se guarda en `notas` porque el contrato no tiene campo propio de unidad.
import React, { useRef, useCallback } from 'react';
import { GripVertical, Trash2, Plus } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS as DndCSS } from '@dnd-kit/utilities';
import { aplicarPegado } from '../utils/tablaMedidas';
import AutoTextarea from './ui/AutoTextarea';
import './TablaMedidasEditor.css';

function FilaSortable({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: DndCSS.Transform.toString(transform), transition };
  return (
    <tr ref={setNodeRef} style={style} className={`tme-tr${isDragging ? ' tme-tr--drag' : ''}`}>
      <td className="tme-td-agarre">
        <span className="tme-agarre" {...attributes} {...listeners} aria-label="Arrastrar para reordenar la talla">
          <GripVertical size={14} aria-hidden="true" />
        </span>
      </td>
      {children}
    </tr>
  );
}

export default function TablaMedidasEditor({ tabla, onChange, rotulo = 'tabla', nota, onNotaChange }) {
  const t = tabla || { columnas: [''], filas: [{ talla: '', valores: [''] }] };
  const columnas = t.columnas || [];
  const filas = t.filas || [];
  const hojaRef = useRef(null);
  // Claves estables por fila para el arrastre (el índice cambia al reordenar).
  const contadorRef = useRef(0);
  const clavesRef = useRef([]);
  while (clavesRef.current.length < filas.length) clavesRef.current.push(`fila-${contadorRef.current++}`);
  const claves = clavesRef.current.slice(0, filas.length);

  const emitir = (next) => onChange && onChange(next);
  const setColumna = (j, v) => emitir({ ...t, columnas: columnas.map((c, k) => (k === j ? v : c)) });
  const agregarColumna = () => emitir({ columnas: [...columnas, ''], filas: filas.map((f) => ({ ...f, valores: [...(f.valores || []), ''] })) });
  const quitarColumna = (j) => emitir({
    columnas: columnas.filter((_, k) => k !== j),
    filas: filas.map((f) => ({ ...f, valores: (f.valores || []).filter((_, k) => k !== j) })),
  });
  const setCelda = (i, j, v) => emitir({
    ...t,
    filas: filas.map((f, k) => {
      if (k !== i) return f;
      if (j === -1) return { ...f, talla: v };
      return { ...f, valores: columnas.map((_, m) => (m === j ? v : (f.valores || [])[m] ?? '')) };
    }),
  });
  const agregarFila = () => emitir({ ...t, filas: [...filas, { talla: '', valores: columnas.map(() => '') }] });
  const quitarFila = (i) => {
    clavesRef.current = claves.filter((_, k) => k !== i);
    emitir({ ...t, filas: filas.filter((_, k) => k !== i) });
  };

  // Enter = bajar a la misma columna de la fila siguiente (desde el encabezado,
  // a la primera fila). Tab ya navega en orden de hoja por el orden del DOM.
  const onTecla = useCallback((e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const fila = Number(e.target.dataset.fila);
    const col = e.target.dataset.col;
    const abajo = hojaRef.current && hojaRef.current.querySelector(`input[data-fila="${fila + 1}"][data-col="${col}"]`);
    if (abajo) abajo.focus();
  }, []);

  // Pegado tipo Excel en celdas del CUERPO: si trae tabulador o salto de línea,
  // rellena desde la celda; un pegado simple sigue el camino nativo.
  const onPegar = (e, fila, col) => {
    const texto = e.clipboardData ? e.clipboardData.getData('text') : '';
    if (!/[\t\n]/.test(texto)) return;
    e.preventDefault();
    emitir(aplicarPegado(t, { fila, col }, texto));
  };

  const sensores = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = claves.indexOf(active.id);
    const to = claves.indexOf(over.id);
    if (from < 0 || to < 0) return;
    clavesRef.current = arrayMove(claves, from, to);
    emitir({ ...t, filas: arrayMove(filas, from, to) });
  };

  return (
    <div className="tme" ref={hojaRef}>
      <DndContext sensors={sensores} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <table className="tme-hoja">
          <thead>
            <tr>
              <th className="tme-th-agarre" aria-hidden="true" />
              <th className="tme-th-talla">Talla</th>
              {columnas.map((c, j) => (
                <th key={j} className="tme-th-col">
                  <input className="tme-celda tme-celda--head" value={c} placeholder="Ej.: Busto"
                    aria-label={`Columna ${j + 1} de la ${rotulo}`}
                    data-fila="-1" data-col={j} onKeyDown={onTecla}
                    onChange={(e) => setColumna(j, e.target.value)} />
                  <button type="button" className="tme-caneca" title="Quitar columna"
                    aria-label={`Quitar la columna ${j + 1} de la ${rotulo}`} onClick={() => quitarColumna(j)}>
                    <Trash2 size={12} aria-hidden="true" />
                  </button>
                </th>
              ))}
              <th className="tme-th-add">
                <button type="button" className="pp-vlink" onClick={agregarColumna}>
                  <Plus size={13} aria-hidden="true" /> Agregar columna
                </button>
              </th>
            </tr>
          </thead>
          <SortableContext items={claves} strategy={verticalListSortingStrategy}>
            <tbody>
              {filas.map((f, i) => (
                <FilaSortable key={claves[i]} id={claves[i]}>
                  <td className="tme-td-talla">
                    <input className="tme-celda tme-celda--talla" value={f.talla} placeholder="Talla"
                      aria-label={`Talla de la fila ${i + 1} de la ${rotulo}`}
                      data-fila={i} data-col="-1" onKeyDown={onTecla}
                      onPaste={(e) => onPegar(e, i, -1)}
                      onChange={(e) => setCelda(i, -1, e.target.value)} />
                  </td>
                  {columnas.map((c, j) => (
                    <td key={j} className="tme-td-valor">
                      <input className="tme-celda" value={(f.valores || [])[j] ?? ''} placeholder="—"
                        aria-label={`${String(c || '').trim() || `Columna ${j + 1}`} de la fila ${i + 1} de la ${rotulo}`}
                        data-fila={i} data-col={j} onKeyDown={onTecla}
                        onPaste={(e) => onPegar(e, i, j)}
                        onChange={(e) => setCelda(i, j, e.target.value)} />
                    </td>
                  ))}
                  <td className="tme-td-accion">
                    <button type="button" className="tme-caneca tme-caneca--fila" title="Quitar talla"
                      aria-label={`Quitar la fila ${i + 1} de la ${rotulo}`} onClick={() => quitarFila(i)}>
                      <Trash2 size={13} aria-hidden="true" />
                    </button>
                  </td>
                </FilaSortable>
              ))}
            </tbody>
          </SortableContext>
        </table>
      </DndContext>
      <button type="button" className="pp-vlink tme-add-talla" onClick={agregarFila}>
        <Plus size={13} aria-hidden="true" /> Agregar talla
      </button>
      {onNotaChange && (
        // Tanda 6 (parte 0.3): la nota crece con el contenido, no se corta.
        <AutoTextarea className="tme-nota" minRows={1} maxRows={6} value={nota || ''}
          placeholder="Medidas en centímetros"
          aria-label="Nota de la tabla (unidad de medida)"
          onChange={(e) => onNotaChange(e.target.value)} />
      )}
      <p className="tme-asi">Así se verá en la tienda.</p>
    </div>
  );
}
